import type { VersionedData } from '../shared/VersionedData';
import { DayMode } from '../routine/enums';
import { Equipment, ExerciseStatus } from './enums';

export interface WorkoutSessionContent {
  readonly dayMode: DayMode;
}

const SESSION_CONTENT_VERSION = 1;

export function toWorkoutSessionData(content: WorkoutSessionContent): VersionedData {
  return { version: SESSION_CONTENT_VERSION, payload: { ...content } };
}

export function fromWorkoutSessionData(
  data: VersionedData | undefined,
): WorkoutSessionContent | undefined {
  if (!data || data.version !== SESSION_CONTENT_VERSION) return undefined;
  const { dayMode } = data.payload;
  if (typeof dayMode !== 'string' || !Object.values(DayMode).includes(dayMode as DayMode)) {
    return undefined;
  }
  return { dayMode: dayMode as DayMode };
}

export interface ExerciseLogContent {
  readonly catalogId: string;
  readonly name: string;
  readonly equipment: Equipment;
  readonly unit: 'reps' | 'minutes' | 'seconds';
  readonly targetValue: number;
  readonly instructions: string;
  readonly supportsWeight: boolean;
  readonly status: ExerciseStatus;
  readonly replacementCatalogId?: string;
  readonly replacedWithCatalogId?: string;
  readonly userWeight?: number;
  readonly weightUnit?: 'kg' | 'lb';
}

const EXERCISE_CONTENT_VERSION = 1;

export function toExerciseLogData(content: ExerciseLogContent): VersionedData {
  return { version: EXERCISE_CONTENT_VERSION, payload: { ...content } };
}

function isExerciseStatus(value: unknown): value is ExerciseStatus {
  return typeof value === 'string' && Object.values(ExerciseStatus).includes(value as ExerciseStatus);
}

function isEquipment(value: unknown): value is Equipment {
  return typeof value === 'string' && Object.values(Equipment).includes(value as Equipment);
}

function isUnit(value: unknown): value is ExerciseLogContent['unit'] {
  return value === 'reps' || value === 'minutes' || value === 'seconds';
}

/** Returns undefined for missing, malformed or future-versioned payloads rather than throwing. */
export function fromExerciseLogData(
  data: VersionedData | undefined,
): ExerciseLogContent | undefined {
  if (!data || data.version !== EXERCISE_CONTENT_VERSION) return undefined;
  const {
    catalogId,
    name,
    equipment,
    unit,
    targetValue,
    instructions,
    supportsWeight,
    status,
    replacementCatalogId,
    replacedWithCatalogId,
    userWeight,
    weightUnit,
  } = data.payload;
  if (
    typeof catalogId !== 'string' ||
    typeof name !== 'string' ||
    !isEquipment(equipment) ||
    !isUnit(unit) ||
    typeof targetValue !== 'number' ||
    typeof instructions !== 'string' ||
    typeof supportsWeight !== 'boolean' ||
    !isExerciseStatus(status)
  ) {
    return undefined;
  }
  return {
    catalogId,
    name,
    equipment,
    unit,
    targetValue,
    instructions,
    supportsWeight,
    status,
    ...(typeof replacementCatalogId === 'string' ? { replacementCatalogId } : {}),
    ...(typeof replacedWithCatalogId === 'string' ? { replacedWithCatalogId } : {}),
    ...(typeof userWeight === 'number' ? { userWeight } : {}),
    ...(weightUnit === 'kg' || weightUnit === 'lb' ? { weightUnit } : {}),
  };
}
